import { useCallback, useEffect, useMemo, useState } from 'react';
import { bankService, billingService } from '../clients/ApiClients';
import { canManageBankConnections, canViewLedger } from '../constants/UserTypes';
import { useUser } from '../hooks/UserContext';
import { apiErrorMessage, apiErrorStatus } from '../utils/apiErrors';

/** One association's money that needs someone; a count the viewer may not see is `null`. */
export interface MoneyAttention {
  hoaId: string;
  /** NSF items still awaiting a decision — only for someone who can open Banking, where they are worked. */
  nsf: number | null;
  /** Payments that came in without a home to post to. */
  unmatched: number | null;
  /** Billing's review queue: conditions waiting on a decision. */
  review: number | null;
}

export interface MoneyAttentionState {
  /** `false`: this viewer sees no money, and nothing was requested. */
  allowed: boolean;
  /** Associations with something to look at; all-zero ones are left out. */
  rows: MoneyAttention[];
  loading: boolean;
  error: string | null;
  /** When the counts were read; they do not follow changes (#670). */
  asOf: Date | null;
  reload: () => void;
}

// Requests in flight at once: a company has dozens of associations.
const CONCURRENCY = 4;

const total = (n: number | null | undefined): number => n ?? 0;

/**
 * Money that needs attention, for Home (#670). Money never syncs, so this
 * is fetched once per visit and on demand — it is not live, and the page
 * says so. A count is requested only from someone the page it previews is
 * open to: NSF items awaiting a decision behind Banking's gate, payments and the review
 * queue behind Billing's. Reads only; nothing here is retried or resent.
 */
export function useMoneyAttention(hoaIds: string[]): MoneyAttentionState {
  const { userType } = useUser();
  const seesNsf = canManageBankConnections(userType);
  const seesBilling = canViewLedger(userType);
  const allowed = seesNsf || seesBilling;

  // A rename arrives through the engine as a new list; only a different
  // set of associations is worth another round of requests.
  const idsKey = useMemo(() => [...hoaIds].sort().join(','), [hoaIds]);

  const [rows, setRows] = useState<MoneyAttention[]>([]);
  const [loading, setLoading] = useState(allowed);
  const [error, setError] = useState<string | null>(null);
  const [asOf, setAsOf] = useState<Date | null>(null);
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!allowed || !idsKey) {
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    setError(null);
    const ids = idsKey.split(',');
    const out: MoneyAttention[] = [];
    let failures = 0;
    let refused = 0;
    let firstError: unknown = null;

    const one = async (hoaId: string): Promise<void> => {
      const [nsf, unmatched, review] = await Promise.all([
        seesNsf
          ? bankService.listHoaNsf({ hoaId, openOnly: true }).then((r) => r.data.open)
          : null,
        seesBilling
          ? billingService
              .listUnmatchedPayments({ hoaId, perPage: 1 })
              .then((r) => r.data.total)
          : null,
        seesBilling
          ? billingService.reviewQueue({ hoaId, perPage: 1 }).then((r) => r.data.total)
          : null,
      ]);
      out.push({ hoaId, nsf, unmatched, review });
    };

    const worker = async (queue: string[]): Promise<void> => {
      for (let id = queue.shift(); id; id = queue.shift()) {
        try {
          await one(id);
        } catch (err) {
          // Not this viewer's association: the server is the last word on
          // whose money they may see, and a refusal is an answer, not a
          // failure to load.
          if (apiErrorStatus(err) === 403) {
            refused += 1;
            continue;
          }
          failures += 1;
          firstError = firstError ?? err;
        }
      }
    };

    const queue = [...ids];
    Promise.all(Array.from({ length: Math.min(CONCURRENCY, ids.length) }, () => worker(queue))).then(
      () => {
        if (!live) return;
        setRows(
          out
            .filter((r) => total(r.nsf) + total(r.unmatched) + total(r.review) > 0)
            .sort((a, b) => ids.indexOf(a.hoaId) - ids.indexOf(b.hoaId))
        );
        setError(
          failures === 0
            ? null
            : failures === ids.length - refused
              ? apiErrorMessage(firstError, "Couldn't load money that needs attention.")
              : `Couldn't load money for ${failures} of ${ids.length - refused} associations.`
        );
        setAsOf(new Date());
        setLoading(false);
      }
    );
    return () => {
      live = false;
    };
  }, [allowed, seesNsf, seesBilling, idsKey, attempt]);

  return { allowed, rows, loading, error, asOf, reload };
}
